import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Button,
  Container,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router-dom';

import CenteredBox from '@/components/CenteredBox';
import useAuth from '@/hooks/useAuth';

const ErrorBoundary = () => {
  const theme = useTheme();
  const error = useRouteError() as Error;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companyName } = user ?? {};

  const errorString =
    error?.message ?? error?.toString() ?? t('errorBoundary.unknownError');
  const subject = `${companyName ?? ''} - ${t('errorBoundary.errorReportSubject')}`;
  const body = `${errorString}`;

  const handleReset = async () => {
    await navigate('/', { replace: true });
  };

  return (
    <Container>
      <CenteredBox
        sx={{
          flexDirection: 'column',
          gap: 3,
          textAlign: 'center',
          py: 4,
        }}
      >
        <ExclamationTriangleIcon
          width={64}
          height={64}
          color={theme.palette.warning.main}
        />
        <Typography variant="h5" component="h1" gutterBottom>
          {t('errorBoundary.errorPage.title')}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {t('errorBoundary.errorPage.contact')}{' '}
          <Link
            href={`mailto:support@fairmatic.com?subject=${subject}&body=${body}`}
            underline="hover"
          >
            {t('common.supportEmail')}
          </Link>{' '}
          {t('errorBoundary.errorPage.persistsMessage')}
        </Typography>

        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 2, maxWidth: '100%', overflow: 'auto' }}>
            <Typography
              variant="body2"
              color="error"
              component="pre"
              sx={{ textAlign: 'left' }}
            >
              {errorString}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleReset}
          sx={{ mt: 2 }}
        >
          {t('errorBoundary.errorPage.backToHome')}
        </Button>
      </CenteredBox>
    </Container>
  );
};

export default ErrorBoundary;
